import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const ReturnContext = createContext();

// Create a provider component
export const ReturnProvider = ({ children }) => {
  // Initialize state with values from localStorage if available
  const [orderData, setOrderData] = useState(() => {
    const savedData = localStorage.getItem('returnOrderData');
    return savedData ? JSON.parse(savedData) : null;
  });
  
  const [selectedItems, setSelectedItems] = useState(() => {
    const savedItems = localStorage.getItem('returnSelectedItems');
    return savedItems ? JSON.parse(savedItems) : [];
  });
  
  const [returnType, setReturnType] = useState(() => {
    const savedType = localStorage.getItem('returnType');
    return savedType || 'return';
  });
  
  const [notes, setNotes] = useState(() => {
    const savedNotes = localStorage.getItem('returnNotes');
    return savedNotes || '';
  });
  
  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem('returnStep');
    return savedStep ? parseInt(savedStep) : 1;
  });
  
  const [submitted, setSubmitted] = useState(() => {
    const savedSubmitted = localStorage.getItem('returnSubmitted');
    return savedSubmitted === 'true';
  });

  // Update localStorage when state changes
  useEffect(() => {
    if (orderData) {
      localStorage.setItem('returnOrderData', JSON.stringify(orderData));
    } else {
      localStorage.removeItem('returnOrderData');
    }
  }, [orderData]);

  useEffect(() => {
    if (selectedItems.length > 0) {
      localStorage.setItem('returnSelectedItems', JSON.stringify(selectedItems));
    } else {
      localStorage.removeItem('returnSelectedItems');
    }
  }, [selectedItems]);

  useEffect(() => {
    localStorage.setItem('returnType', returnType);
  }, [returnType]);

  useEffect(() => {
    localStorage.setItem('returnNotes', notes);
  }, [notes]);

  useEffect(() => {
    localStorage.setItem('returnStep', step.toString());
  }, [step]);

  useEffect(() => {
    localStorage.setItem('returnSubmitted', submitted.toString());
  }, [submitted]);

  // Function to reset all state (e.g., after submission or when starting a new return)
  const resetReturnState = () => {
    setOrderData(null);
    setSelectedItems([]);
    setReturnType('return');
    setNotes('');
    setStep(1);
    setSubmitted(false);
    
    // Clear localStorage
    localStorage.removeItem('returnOrderData');
    localStorage.removeItem('returnSelectedItems');
    localStorage.removeItem('returnType');
    localStorage.removeItem('returnNotes');
    localStorage.removeItem('returnStep');
    localStorage.removeItem('returnSubmitted');
  };

  // The value that will be provided to consumers of this context
  const value = {
    orderData,
    setOrderData,
    selectedItems,
    setSelectedItems,
    returnType,
    setReturnType,
    notes,
    setNotes,
    step,
    setStep,
    submitted,
    setSubmitted,
    resetReturnState
  };

  return (
    <ReturnContext.Provider value={value}>
      {children}
    </ReturnContext.Provider>
  );
};

// Custom hook to use the return context
export const useReturnContext = () => {
  const context = useContext(ReturnContext);
  if (context === undefined) {
    throw new Error('useReturnContext must be used within a ReturnProvider');
  }
  return context;
};
